import styles from "./repository.module.scss";
import repositoryIcon from "../../images/repository.svg";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../hooks/useAppSelector";
import { fetchMasterPlanModificationData } from "../../services/reducers/master-plan-modification";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Roles } from "../../types/roles";
import {
  fetchUtilities,
  resetTableState,
  setReceiveNotifications,
} from "../../services/reducers/utilities-journal";
import { useTranslation } from "react-i18next";
import Button from "../../components/ui-kit/button/button";
import Modal from "../../shared/modal/modal";
import StructureModal from "./structure-modal/structure-modal";

const RepositoryPage = () => {
  const userRoles = useAppSelector((store) => store.user.user?.roles);
  const { layers } = useAppSelector((state) => state.map);
  const { notificationCount, receiveNotifications } = useAppSelector(
    (state) => state.masterUtilities,
  );
  const { user } = useAppSelector((state) => state.user);
  const [isStructureModalOpen, setIsStructureModalOpen] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const isKrookieActive = location.pathname.includes("krookie");
  const isReportsActive = location.pathname.includes("reports");
  const isUtilitiesActive = location.pathname.includes("utilities");

  const storageKey = useMemo(
    () => `receiveNotifications_${user?.userName}`,
    [user?.userName],
  );

  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored !== null) {
      dispatch(setReceiveNotifications(JSON.parse(stored)));
    }
  }, [dispatch, storageKey]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(receiveNotifications));
  }, [receiveNotifications, storageKey]);

  useEffect(() => {
    const hasAccess =
      userRoles?.includes(Roles.DepartmentSpecialist) ||
      userRoles?.includes(Roles.Contractor);

    if (hasAccess) {
      dispatch(fetchUtilities());
    }

    return () => {
      dispatch(resetTableState());
    };
  }, [userRoles]);

  useEffect(() => {
    if (layers && layers.length > 0) {
      dispatch(fetchMasterPlanModificationData());
    }
  }, [layers]);

  const { t } = useTranslation();

  const toggleStructureModal = () => {
    setIsStructureModalOpen((prevState) => !prevState);
  };

  return (
    <>
      <div className={styles.repositoryPage}>
        <div className={styles.leftPanel}>
          <h1>{t("repository.title")}</h1>
          <ul className={styles.repositoriesList}>
            {userRoles?.includes(Roles.KrookieJournal) && (
              <li
                onClick={() => navigate("krookie")}
                className={isKrookieActive ? styles.active : ""}
              >
                <img
                  src={repositoryIcon}
                  alt={t("repository.krookieJournal")}
                />
                {t("repository.krookieJournal")}
              </li>
            )}
            {userRoles?.includes(Roles.ReportsJournal) && (
              <li
                onClick={() => navigate("reports")}
                className={isReportsActive ? styles.active : ""}
              >
                <img
                  src={repositoryIcon}
                  alt={t("repository.reportsJournal")}
                />
                {t("repository.reportsJournal")}
              </li>
            )}
            {(userRoles?.includes(Roles.Contractor) ||
              userRoles?.includes(Roles.DepartmentSpecialist)) && (
              <li
                onClick={() => navigate("utilities")}
                className={isUtilitiesActive ? styles.active : ""}
              >
                <img
                  src={repositoryIcon}
                  alt={t("repository.utilitiesJournal")}
                />
                {t("repository.utilities")}
                {notificationCount > 0 && receiveNotifications && (
                  <div className={styles.notification}>{notificationCount}</div>
                )}
              </li>
            )}
          </ul>
          {isUtilitiesActive && (
            <div className={styles.structureDownloadBlock}>
              <span className={styles.description}>
                {t("repository.structureDescription")}
              </span>
              <Button
                onClick={toggleStructureModal}
                className={styles.btn}
                type="withIcon"
              >
                <span className={styles.downloadIcon} />
                {t("repository.downloadStructure")}
              </Button>
            </div>
          )}
        </div>
        <div className={styles.contentWrapper}>
          <Outlet />
        </div>
      </div>
      {isStructureModalOpen && (
        <StructureModal onClose={toggleStructureModal} />
      )}
    </>
  );
};

export default RepositoryPage;
